import { CalendarDate } from '@internationalized/date';
import { Money, PaymentMethod } from '@rematter/frontend-lib-api-client';
import { atom, Getter } from 'jotai';

import { paymentApplyToBillAtom } from '../_graphql/mutationPaymentApplyToBill';
import { paymentApplyToInvoiceAtom } from '../_graphql/mutationPaymentApplyToInvoice';
import { paymentRemoveFromBillAtom } from '../_graphql/mutationPaymentRemoveFromBill';
import { paymentRemoveFromInvoiceAtom } from '../_graphql/mutationPaymentRemoveFromInvoice';
import { paymentUpdateAtom } from '../_graphql/mutationPaymentUpdate';
import { paymentVoidAtom } from '../_graphql/mutationPaymentVoid';
import { paymentDetailQueryAtom } from '../_graphql/queryPaymentDetail';
import { financeRoutes } from '../routes';
import { PaymentRecord } from '../types';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

export type PaymentDetailsTab = 'info' | 'linked-documents';

export type DraftPaymentInfo = {
  documentNumber: string | null;
  transactionDate: CalendarDate | null;
  amount: Money | null;
  paymentMethod: PaymentMethod | null;
  checkNumber: string | null;
  checkMemo: string | null;
  achNumber: string | null;
  wireNumber: string | null;
};

export const $paymentDetailPathParams = atomFromRoutePath(
  financeRoutes.paymentDetail,
);

export const $paymentId = atom((get) =>
  get($paymentDetailPathParams)?.id
    ? String(get($paymentDetailPathParams)?.id)
    : null,
);

export const $tab = atomFromQueryString.string<PaymentDetailsTab>('tab');

export const $remotePaymentDetail = paymentDetailQueryAtom((get) => ({
  variables: { id: get($paymentId) ?? '' },
  skip: !get($paymentId),
}));

const refetchPaymentDetail = (get: Getter) => {
  const paymentId = get($paymentId);
  if (!paymentId) {
    return;
  }
  get($remotePaymentDetail).refetch?.();
};

export const $updatePayment = paymentUpdateAtom({
  onSuccess: (get, set) => {
    set($currentDraftPaymentInfo, null);
    refetchPaymentDetail(get);
  },
});

export const $voidPayment = paymentVoidAtom({
  onSuccess: (get) => {
    refetchPaymentDetail(get);
  },
});

export const $isPaymentLoading = atom((get) => {
  const data = get($remotePaymentDetail);
  return data?.isLoading ?? false;
});

export const $paymentError = atom((get) => {
  const data = get($remotePaymentDetail);
  return data?.error ?? null;
});

export const $payment = atom((get) => {
  const result = get($remotePaymentDetail);
  return (result?.data as { payment: PaymentRecord | null } | undefined)
    ?.payment;
});

export const $draftPaymentInfo = atom<Map<string, DraftPaymentInfo>>(new Map());

export const $currentDraftPaymentInfo = atom(
  (get) => {
    const paymentId = get($paymentId);
    const draftMap = get($draftPaymentInfo);
    return paymentId ? (draftMap.get(paymentId) ?? null) : null;
  },
  (get, set, info: DraftPaymentInfo | null) => {
    const paymentId = get($paymentId);
    if (!paymentId) {
      return;
    }

    set($draftPaymentInfo, (prev) => {
      const newMap = new Map(prev);
      if (info === null) {
        newMap.delete(paymentId);
      } else {
        newMap.set(paymentId, info);
      }
      return newMap;
    });
  },
);

export const $effectivePaymentInfo = atom((get) => {
  const result = get($remotePaymentDetail);
  const draftInfo = get($currentDraftPaymentInfo);
  const remotePayment = (
    result?.data as { payment: PaymentRecord | null } | undefined
  )?.payment;

  if (!remotePayment) {
    return null;
  }

  if (draftInfo !== null) {
    return {
      ...remotePayment,
      documentNumber: draftInfo.documentNumber ?? remotePayment.documentNumber,
      transactionDate:
        draftInfo.transactionDate ?? remotePayment.transactionDate,
      amount: draftInfo.amount ?? remotePayment.amount,
      paymentMethod: draftInfo.paymentMethod ?? remotePayment.paymentMethod,
      checkNumber: draftInfo.checkNumber,
      checkMemo: draftInfo.checkMemo,
      achNumber: draftInfo.achNumber,
      wireNumber: draftInfo.wireNumber,
    };
  }

  return remotePayment;
});

export const $hasDraftInfoChanges = atom((get) => {
  const result = get($remotePaymentDetail);
  const draftInfo = get($currentDraftPaymentInfo);

  if (!result?.data || draftInfo === null) {
    return false;
  }

  const payment = (result.data as { payment: PaymentRecord | null }).payment;
  if (!payment) {
    return false;
  }

  const documentNumberChanged =
    draftInfo.documentNumber !== payment.documentNumber;
  const transactionDateChanged =
    (draftInfo.transactionDate?.toString() ?? null) !==
    (payment.transactionDate?.toString() ?? null);
  const amountChanged =
    draftInfo.amount?.amount !== payment.amount?.amount ||
    draftInfo.amount?.currencyCode !== payment.amount?.currencyCode;
  const paymentMethodChanged =
    draftInfo.paymentMethod !== payment.paymentMethod;
  const checkNumberChanged = draftInfo.checkNumber !== payment.checkNumber;
  const checkMemoChanged = draftInfo.checkMemo !== payment.checkMemo;
  const achNumberChanged = draftInfo.achNumber !== payment.achNumber;
  const wireNumberChanged = draftInfo.wireNumber !== payment.wireNumber;

  return (
    documentNumberChanged ||
    transactionDateChanged ||
    amountChanged ||
    paymentMethodChanged ||
    checkNumberChanged ||
    checkMemoChanged ||
    achNumberChanged ||
    wireNumberChanged
  );
});

export const $isDirty = atom((get) => get($hasDraftInfoChanges));

export const $discardAllDrafts = atom(null, (get, set) => {
  set($currentDraftPaymentInfo, null);
});

export const $initDraftFromPayment = atom(
  null,
  (get, set, payment: PaymentRecord) => {
    set($currentDraftPaymentInfo, {
      documentNumber: payment.documentNumber,
      transactionDate: payment.transactionDate,
      amount: payment.amount,
      paymentMethod: payment.paymentMethod,
      checkNumber: payment.checkNumber ?? null,
      checkMemo: payment.checkMemo ?? null,
      achNumber: payment.achNumber ?? null,
      wireNumber: payment.wireNumber ?? null,
    });
  },
);

export const $applyPaymentToBill = paymentApplyToBillAtom({
  onSuccess: (get) => {
    refetchPaymentDetail(get);
  },
});

export const $applyPaymentToInvoice = paymentApplyToInvoiceAtom({
  onSuccess: (get) => {
    refetchPaymentDetail(get);
  },
});

export const $removePaymentFromBill = paymentRemoveFromBillAtom({
  onSuccess: (get) => {
    refetchPaymentDetail(get);
  },
});

export const $removePaymentFromInvoice = paymentRemoveFromInvoiceAtom({
  onSuccess: (get) => {
    refetchPaymentDetail(get);
  },
});
